import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface RevenueStream {
  id: string
  name: string
  type: string
  current_mrr: number
  growth_rate: number
  churn_rate: number
  customer_count: number
}

interface Opportunity {
  id: string
  customer_name: string
  type: string
  potential_value: number
  probability: number
  status: string
  priority: string
  ai_recommendation: string
}

export default function RevenueIntelPage() {
  const [streams, setStreams] = useState<RevenueStream[]>([])
  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [streamRes, oppRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/revenueintel/streams'),
      fetch(API_BASE + '/revenueintel/opportunities'),
      fetch(API_BASE + '/revenueintel/statistics')
    ])
    if (streamRes.ok) setStreams((await streamRes.json()).streams || [])
    if (oppRes.ok) setOpportunities((await oppRes.json()).opportunities || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'won': case 'qualified': return 'bg-green-100 text-green-800'
      case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'new': return 'bg-yellow-100 text-yellow-800'
      case 'lost': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600'
      case 'medium': return 'text-yellow-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Revenue Intelligence</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Opportunity</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total MRR</div>
              <div className="text-2xl font-bold text-green-600">${(statistics.total_mrr / 1000000).toFixed(1)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total ARR</div>
              <div className="text-2xl font-bold text-blue-600">${(statistics.total_arr / 1000000).toFixed(1)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">NRR</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.net_revenue_retention}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Pipeline</div>
              <div className="text-2xl font-bold">${(statistics.pipeline_value / 1000000).toFixed(2)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Opportunities</div>
              <div className="text-2xl font-bold">{statistics.open_opportunities}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Revenue Streams</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stream</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MRR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customers</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {streams.map(stream => (
                  <tr key={stream.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{stream.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{stream.type.replace('_', ' ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">${stream.current_mrr.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${stream.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {stream.growth_rate >= 0 ? '+' : ''}{stream.growth_rate}%
                      </div>
                      <div className="text-xs text-gray-500">{stream.churn_rate}% churn</div>
                    </td>
                    <td className="px-6 py-4 text-sm">{stream.customer_count.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Top Opportunities</h3>
            <div className="space-y-3">
              {opportunities.filter(o => o.status !== 'won' && o.status !== 'lost').slice(0, 10).map(opp => (
                <div key={opp.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm truncate flex-1">{opp.customer_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ml-2 ${getStatusColor(opp.status)}`}>{opp.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{opp.type.replace('_', ' ')}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-medium">${opp.potential_value.toLocaleString()}</span>
                    <span className={`text-xs ${getPriorityColor(opp.priority)}`}>{opp.priority}</span>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">{opp.ai_recommendation}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
